import { useEffect, useRef, useState } from 'react';

import { searchReportPatients } from './api';
import { tx } from './i18n';
import { errorMessage } from './ui';

// Finding a patient is the same problem everywhere it appears: type part of a
// code or a name, get candidates, and end up holding a real record. Only the
// rendering differs, so this is a hook rather than a component -- a screen that
// wants a datalist, a dropdown or a result list can have any of them.
//
// A patient is identified by `id` and nothing else. `rowId` stays null until a
// real record has been resolved, so a half-typed search can never be submitted
// as though it were a patient.

const SEARCH_MIN_LENGTH = 2;
const DEBOUNCE_MS = 250;

export function lookupHint() {
  return tx('ui.patient_lookup.hint');
}

// Patients are referred to by name and id together: an id alone identifies a row
// but tells nobody who it is.
function describe(patient) {
  return patient.name
    ? tx('ui.patient_lookup.named_patient', { name: patient.name, id: patient.id })
    : tx('ui.patient_lookup.patient_number', { id: patient.id });
}

// A pipeline's requires_patient_fields maps each patient fact it needs to the name
// its own parameter takes: {"sex":"GENDER"}, {"age":"AGE","sex":"SEX"}, or {} for
// none. Only the facts matter here, spelled one way (age, sex).
const FACT_NAMES = { age: 'age', sex: 'sex', gender: 'sex' };

export function requiredPatientFacts(fields) {
  const declared = typeof fields === 'string' ? JSON.parse(fields) : fields;
  if (!declared || typeof declared !== 'object') return [];
  const names = Array.isArray(declared) ? declared : Object.keys(declared);
  return [...new Set(names.map((name) => {
    const key = String(name).trim().toLowerCase();
    return FACT_NAMES[key] || key;
  }).filter(Boolean))];
}

// The facts a patient lacks, as labels. Age comes from the date of birth. The
// pipelines take Male or Female, so 'other', blank, or anything the importer could
// not read is as unusable as a missing sex.
export function missingPatientFacts(patient, facts) {
  return facts.flatMap((fact) => {
    if (fact === 'age') return patient.dateOfBirth ? [] : [tx('ui.patient_lookup.date_of_birth')];
    if (fact === 'sex') return patient.gender === 'male' || patient.gender === 'female' ? [] : [tx('ui.patient_lookup.recorded_sex')];
    return [fact];
  });
}

export function missingFactsMessage(patient, missing) {
  return tx('ui.patient_lookup.missing_required_fields', {
    patient: describe(patient),
    fields: new Intl.ListFormat(document.documentElement.lang, {
      style: 'long', type: 'conjunction',
    }).format(missing),
  });
}

export function usePatientLookup({
  minLength = SEARCH_MIN_LENGTH, debounceMs = DEBOUNCE_MS,
} = {}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [matches, setMatches] = useState([]);
  const [patient, setPatient] = useState(null);
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  // A result chosen from a list (or loaded by its exact id) is already
  // authoritative. Keep the debounce effect from searching that displayed id
  // again and overwriting the selection when the search endpoint has a longer
  // minimum term length.
  const chosenRef = useRef(null);

  // Held in a ref so a caller can react to a resolution without the effect
  // depending on the callback and re-running on every render.
  const onResolveRef = useRef(null);

  useEffect(() => {
    const trimmed = term.trim();
    const timer = setTimeout(() => setDebounced(trimmed), debounceMs);
    return () => clearTimeout(timer);
  }, [term, debounceMs]);

  useEffect(() => {
    if (chosenRef.current !== null
        && String(chosenRef.current.id) === debounced) {
      return undefined;
    }
    if (debounced.length < minLength) {
      setMatches([]);
      setPatient(null);
      setStatus('idle');
      setMessage('');
      return undefined;
    }

    let active = true;
    setStatus('loading');
    setMessage('');

    searchReportPatients(debounced)
      .then(({ patients, truncated }) => {
        if (!active) return;
        setMatches(patients);

        // Resolved only when the term is exactly one patient's id. Anything
        // else is a shortlist, and a shortlist is not a patient.
        const exact = patients.find((found) => String(found.id) === debounced);
        if (exact) {
          setPatient(exact);
          setStatus('found');
          setMessage('');
          if (onResolveRef.current) onResolveRef.current(exact);
          return;
        }

        setPatient(null);
        setStatus(patients.length === 0 ? 'missing' : 'choosing');
        setMessage(patients.length === 0
          ? tx('ui.patient_lookup.no_matches')
          : tx('ui.patient_lookup.matches_pick_one', {
            count: `${patients.length}${truncated ? '+' : ''}`,
          }));
      })
      .catch((error) => {
        if (!active) return;
        setMatches([]);
        setPatient(null);
        setStatus('missing');
        setMessage(errorMessage(error));
      });

    return () => { active = false; };
  }, [debounced, minLength]);

  function reset() {
    chosenRef.current = null;
    setTerm('');
    setDebounced('');
    setMatches([]);
    setPatient(null);
    setStatus('idle');
    setMessage('');
  }

  // Picking from a list resolves immediately rather than waiting for the search
  // to run again on the id that was just chosen.
  function choose(match) {
    chosenRef.current = match;
    setTerm(String(match.id));
    setDebounced(String(match.id));
    setMatches([match]);
    setPatient(match);
    setStatus('found');
    setMessage('');
    if (onResolveRef.current) onResolveRef.current(match);
  }

  return {
    term,
    setTerm: (value) => {
      chosenRef.current = null;
      setTerm(value);
    },
    matches,
    patient,
    // What gets submitted. Null until a real record is held.
    rowId: patient ? patient.id ?? null : null,
    status,
    message,
    reset,
    choose,
    onResolve: (handler) => { onResolveRef.current = handler; },
    // True when something was typed that never became a patient -- the state a
    // form must refuse to submit in.
    unresolved: term.trim() !== '' && !patient,
  };
}
